// ============================================================
// Tour Expense System — Calculation Engine
// ============================================================

export function calculateExpenseSplit(expense, families) {
  const amount = parseFloat(expense.amount) || 0;
  switch (expense.splitType) {
    case 'equal':
      return { family1Share: +(amount / 2).toFixed(2), family2Share: +(amount / 2).toFixed(2) };
    case 'custom':
      return {
        family1Share: +(parseFloat(expense.family1Share) || 0).toFixed(2),
        family2Share: +(parseFloat(expense.family2Share) || 0).toFixed(2),
      };
    case 'per_person': {
      const f1Count = families.family1?.members.length || 0;
      const f2Count = families.family2?.members.length || 0;
      const total = f1Count + f2Count;
      if (total === 0) return { family1Share: 0, family2Share: 0 };
      const pp = amount / total;
      return { family1Share: +(pp * f1Count).toFixed(2), family2Share: +(pp * f2Count).toFixed(2) };
    }
    case 'weighted': {
      const weights = expense.memberWeights || {};
      let f1w = 0, f2w = 0;
      families.family1?.members.forEach(m => { f1w += parseFloat(weights[m.id]) || 0; });
      families.family2?.members.forEach(m => { f2w += parseFloat(weights[m.id]) || 0; });
      const tw = f1w + f2w;
      if (tw === 0) return { family1Share: 0, family2Share: 0 };
      const pw = amount / tw;
      return { family1Share: +(pw * f1w).toFixed(2), family2Share: +(pw * f2w).toFixed(2) };
    }
    case 'family1_only':
      return { family1Share: +amount.toFixed(2), family2Share: 0 };
    case 'family2_only':
      return { family1Share: 0, family2Share: +amount.toFixed(2) };
    default:
      return { family1Share: +(amount / 2).toFixed(2), family2Share: +(amount / 2).toFixed(2) };
  }
}

export function getFamilyForMember(memberId, families) {
  for (const fid of ['family1', 'family2']) {
    if (families[fid]?.members.some(m => m.id === memberId)) return fid;
  }
  return null;
}

export function getMemberById(memberId, families) {
  for (const fid of ['family1', 'family2']) {
    const m = families[fid]?.members.find(m => m.id === memberId);
    if (m) return { ...m, familyId: fid };
  }
  return null;
}

export function getAllMembers(families) {
  const result = [];
  for (const fid of ['family1', 'family2']) {
    const f = families[fid];
    if (f?.members) f.members.forEach(m => result.push({ ...m, familyId: fid, familyName: f.name }));
  }
  return result;
}

export function calculateFamilyTotals(expenses, families) {
  let f1Paid = 0, f2Paid = 0, f1Should = 0, f2Should = 0;
  expenses.forEach(exp => {
    const amount = parseFloat(exp.amount) || 0;
    const payer = getFamilyForMember(exp.paidBy, families);
    if (payer === 'family1') f1Paid += amount;
    else if (payer === 'family2') f2Paid += amount;
    const split = calculateExpenseSplit(exp, families);
    f1Should += split.family1Share;
    f2Should += split.family2Share;
  });
  return {
    family1: { paid: +f1Paid.toFixed(2), shouldPay: +f1Should.toFixed(2), difference: +(f1Paid - f1Should).toFixed(2) },
    family2: { paid: +f2Paid.toFixed(2), shouldPay: +f2Should.toFixed(2), difference: +(f2Paid - f2Should).toFixed(2) },
    grandTotal: +(f1Paid + f2Paid).toFixed(2),
  };
}

export function calculateSettlement(expenses, payments, families) {
  const totals = calculateFamilyTotals(expenses, families);
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  let payerFamily, receiverFamily, amount;
  if (totals.family1.difference > 0) {
    payerFamily = 'family2'; receiverFamily = 'family1'; amount = +totals.family1.difference.toFixed(2);
  } else if (totals.family2.difference > 0) {
    payerFamily = 'family1'; receiverFamily = 'family2'; amount = +totals.family2.difference.toFixed(2);
  } else {
    payerFamily = null; receiverFamily = null; amount = 0;
  }
  const remaining = +(amount - totalPaid).toFixed(2);
  return { payerFamily, receiverFamily, amount, totalSettlementPaid: +totalPaid.toFixed(2), remaining: Math.max(0, remaining), isSettled: remaining <= 0, totals };
}

export function calculateTotalsByCategory(expenses, families) {
  const keys = ['accommodation','food','transport','entertainment','shopping','misc'];
  const result = {};
  keys.forEach(k => { result[k] = { total: 0, family1Share: 0, family2Share: 0, count: 0 }; });
  expenses.forEach(exp => {
    const cat = exp.category || 'misc';
    if (!result[cat]) result[cat] = { total: 0, family1Share: 0, family2Share: 0, count: 0 };
    const split = calculateExpenseSplit(exp, families);
    result[cat].total += parseFloat(exp.amount) || 0;
    result[cat].family1Share += split.family1Share;
    result[cat].family2Share += split.family2Share;
    result[cat].count++;
  });
  for (const k in result) {
    result[k].total = +result[k].total.toFixed(2);
    result[k].family1Share = +result[k].family1Share.toFixed(2);
    result[k].family2Share = +result[k].family2Share.toFixed(2);
  }
  return result;
}

export function getStats(expenses, payments, families) {
  const totals = calculateFamilyTotals(expenses, families);
  const byCategory = calculateTotalsByCategory(expenses, families);
  const settlement = calculateSettlement(expenses, payments, families);
  let highestCat = 'misc', highestAmt = 0;
  for (const cat in byCategory) {
    if (byCategory[cat].total > highestAmt) { highestAmt = byCategory[cat].total; highestCat = cat; }
  }
  const dates = expenses.map(e => e.date).filter(Boolean);
  let dailyAvg = 0;
  if (dates.length > 0) {
    const minD = new Date(Math.min(...dates.map(d => new Date(d))));
    const maxD = new Date(Math.max(...dates.map(d => new Date(d))));
    const days = Math.max(1, Math.ceil((maxD - minD) / 86400000) + 1);
    dailyAvg = +(totals.grandTotal / days).toFixed(2);
  }
  return { totalExpenses: expenses.length, grandTotal: totals.grandTotal, highestCategory: highestCat, dailyAverage: dailyAvg, settlement, byCategory };
}
